<template>
  <div>
    <!-- Dialogs -->
    <ConfirmDialog 
      :model="showDeleteDialog" 
      @confirm="attemptDeleteDisplay"
      @cancel="() => displayToDelete = undefined">
      <template v-if="displayToDelete">{{ `Přejete si smazat obrazovku s názvem "${displayToDelete.name}"?` }}</template>
      Tato operace je nevratná.
    </ConfirmDialog>
    <Alert 
      :model="errorOnDelete" 
      @confirm="() => errorOnDelete = false">
      Zvolenou obrazovku se nepodařilo smazat.
    </Alert>
    <!-- Module header -->
    <BackButton :target="{name: 'displaysUnits'}">Výběr fakult a součástí</BackButton>
    <ModuleHeader>
      <h2 id="dashboard-header">
        Informační panely{{ didLoadAdministrators ? ` - ${selectedUnit.localizedAttributes.cz.name}` : '' }}
      </h2>
      <v-btn id="displays-guide" class="guide-button" variant="outlined" color="grey-darken-1" @click="goToGuide()">
        <v-icon class="mr-2" left>info</v-icon> Jste tu poprvé?
      </v-btn>
    </ModuleHeader>
    <!-- Module navigation -->
    <LightButton @click="router.push({name:'announcements',query:{desc: 'false'}})" class="mt-1 pl-0 pull-left">Spravovat oznámení</LightButton>
    <LightButton @click="router.push({name:'announcementTypes'})" class="mt-1 pull-left">Spravovat typy oznámení</LightButton>
    <!-- Dashboard -->
    <Loading v-if="!didLoadDashboard && !didLoadDisplays" class="mt-12"/>
    <v-container fluid>
      <v-container class="text-center mt-4" v-if="getDisplays.length === 0 && didLoadDisplays">
        <img :src="Placeholder" id="no-displays-placeholder" />
        <v-row>
          <v-col>
            <p class="mb-10">Dosud nebyly přidány žádné obrazovky...</p>
          </v-col>
        </v-row>
        <PrimaryButton id="add-display" :to="{name: 'displayDetail', params: {displayId: 'new'}}">+ Nová obrazovka</PrimaryButton>
      </v-container>
      <div v-if="getDisplays.length > 0 && didLoadDisplays">
        <div v-if="responseMessage.activeDisplays.length === 0 && didLoadDashboard" class="mt-4">
          Aktuálně nejsou v provozu žádné obrazovky...
          <InfoButton bottom>Zobrazování obsahu je na cílovém zařízení možné spustit přechodem na webovou adresu přidruženou zvolené obrazovce.</InfoButton>
        </div>
        <div v-if="responseMessage.activeDisplays.length > 0">
          <ModuleHeader class="mt-4 mb-8">
            <RefreshButton class="refresh-icon" noMargin customIcon="reset_tv" @click="refreshDevices" title="Vynutit obnovení prezentace na cílových zařízeních">
              <h4 class="mr-4">Právě v provozu</h4>
            </RefreshButton>
          </ModuleHeader>
          <v-row>
            <v-col v-for="(display, idx) in responseMessage.activeDisplays" :key="`display-${idx}-${display.name}`" cols="12" sm="6" md="4" lg="3">
              <DashboardItem :display="display" />
            </v-col>
          </v-row>
        </div>
        <Loading v-if="!didLoadDashboard" class="mt-12"/>
        <!-- List of displays -->
        <ModuleHeader class="mt-4 mb-3">
          <RefreshButton class="refresh-icon" noMargin @click="refetchDisplays" title="Aktualizovat data">
            <h4 class="mr-4">Neaktivní obrazovky</h4>
          </RefreshButton>
          <PrimaryButton id="add-display" :to="{name: 'displayDetail', params: {displayId: 'new'}}">+ Nová</PrimaryButton>
        </ModuleHeader>
        <div class="displays-table">
          <v-data-table
            class="d-none d-sm-block"
            :headers="headers"
            :items="inactiveDisplays"
            items-per-page="25"
          >
            <template v-slot:item.name="{ item }">
              <TitledText id="display-name" :title="item.name" :text="ellipsis(item.name, 55)" />
            </template>
            <template v-slot:item.id="{ item }">
              <a class="link light-green--text" :href="getDisplayUrl(item.id)" target="_blank" title="Otevřít obrazovku v novém okně">{{ getDisplayUrl(item.id) }}</a>
            </template>
            <template v-slot:item.mode="{ item }">
                <TitledText :title="`Maximálně ${item.maxParallels} paralelní oznámení`" :text="item.maxParallels.toString()" />
              </template>
              <template v-slot:item.period="{ item }">
                <TitledText :title="`Obnova po ${item.refreshPeriod} vteřinách`" :text="item.refreshPeriod.toString()" />
              </template>
            <template v-slot:item.actions="{ item }">
              <EditIcon id="edit-display" @click="goToDetail(item.id)" title="Upravit obrazovku"/>
              <DeleteIcon id="remove-display" @click="() => displayToDelete = item" title="Smazat obrazovku"/>
            </template>
          </v-data-table>
          <!-- List of displays for smaller screens -->
          <v-data-iterator
              class="d-block d-sm-none mt-1"
              :items="getDisplays"
              :hide-default-footer="true"
              :disable-pagination="true"
              :hide-default-header="false"
            >
            <template v-slot:no-data>Žádná dostupná data</template>
            <template v-slot:default="props">
              <v-row>
                <v-col v-for="item in props.items" :key="item.raw.id" cols="12" class="pt-2">
                  <MobileDataTableRow
                    @click="goToDetail(item.raw.id)"
                    @action="() => displayToDelete = item.raw"
                  >
                    <template v-slot:title>{{ item.raw.name }}</template>
                    <template v-slot:icon1>link</template>
                    <template v-slot:subtitle1><a :href="getDisplayUrl(item.raw.id)">{{ getDisplayUrl(item.raw.id) }}</a></template>
                    <template v-slot:action_button>delete_outline</template>
                    <template v-slot:label_header1>PARALEL. ZOBRAZENÍ</template>
                    <template v-slot:label_content1>{{ item.raw.maxParallels }}</template>
                    <template v-slot:label_header2>PERIODA OBNOVY [s]</template>
                    <template v-slot:label_content2>{{ item.raw.refreshPeriod }}</template>
                  </MobileDataTableRow>
                </v-col>
              </v-row>
            </template>
          </v-data-iterator>
        </div>
      </div>
    </v-container>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import SockJS from 'sockjs-client';
import Stomp from 'webstomp-client';
import { config } from '@/config';
import Placeholder from '../../assets/displays/no_displays.svg?url';
import { ellipsis } from '@/filters/ellipsis';
import { displaysHeaders } from './displays-helpers';
import Alert from '@/components/dialogs/Alert';
import ConfirmDialog from '@/components/dialogs/ConfirmDialog';
import DashboardItem from "@/components/displays/DashboardItem";
import LightButton from "@/components/buttons/LightButton";
import InfoButton from "@/components/buttons/InfoButton";
import PrimaryButton from "@/components/buttons/PrimaryButton";
import RefreshButton from "@/components/buttons/RefreshButton";
import BackButton from "@/components/layout/BackButton";
import Loading from "@/components/Loading";
import DeleteIcon from "@/components/buttons/DeleteIcon";
import EditIcon from "@/components/buttons/EditIcon";
import MobileDataTableRow from '@/components/layout/MobileDataTableRow.vue';
import ModuleHeader from "@/components/layout/ModuleHeader";
import TitledText from "@/components/text/TitledText";

const store = useStore();
const route = useRoute();
const router = useRouter();

const connected = ref(false);
const didLoadDashboard = ref(false);
const requestMessage = reactive({
  connectionId: undefined,
  unitId: undefined,
});
let responseMessage = reactive({
  activeDisplays: [],
});
const displayToDelete = ref(undefined);
const errorOnDelete = ref(false);
const headers = ref(displaysHeaders);
const socket = ref(null);
const stompClient = ref(null);

const getDisplays = computed(() => store.getters['displays/getDisplays']);
const didLoadDisplays = computed(() => store.getters['displays/didLoadDisplays']);
const getAdministrators = computed(() => store.getters['displaysAdmins/getAdministrators']);
const didLoadAdministrators = computed(() => store.getters['displaysAdmins/didLoadAdministrators']);

const showDeleteDialog = computed(() => displayToDelete.value !== undefined);
const inactiveDisplays = computed(() => getDisplays.value.filter(display => !responseMessage.activeDisplays?.some(other => other.id === display.id)));
const selectedUnit = computed(() => {
  const username = store.getters['user/username'];
  return getAdministrators.value.find((user) => user.xlogin === username)?.faculties.find(
    (faculty) => faculty.id === Number(route.params.unitId)
  );
});

onMounted(async () => {
  await store.dispatch('displaysAdmins/fetchAdministrators');
  await store.dispatch('displays/fetchDisplays', route.params.unitId);
  requestMessage.connectionId = crypto.randomUUID();
  requestMessage.unitId = route.params.unitId;
  window.addEventListener('beforeunload', handler);
  connect();
});

onBeforeUnmount(() => {
  handler();
});

const refetchDisplays = () => store.dispatch('displays/fetchDisplays', route.params.unitId);

const refreshDevices = () => store.dispatch('displays/refreshDisplays');

const attemptDeleteDisplay = () => {
  store
    .dispatch('displays/deleteDisplay', displayToDelete.value.id)
    .catch(() => {
      errorOnDelete.value = true;
    });
  displayToDelete.value = undefined;
};

const goToDetail = (displayId) => {
  router.push({
    name: 'displayDetail',
    params: { displayId },
  });
};

const goToGuide = () => router.push({ name: 'displaysGuide' });

const getDisplayUrl = (id) => `${config.displaysUrl}?id=${id}`;

const connect = () => {
  if (requestMessage.connectionId) {
    socket.value = new SockJS(config.serverUrl + 'socket');
    stompClient.value = Stomp.over(socket.value);
    stompClient.value.connect(
      {},
      () => {
        connected.value = true;
        stompClient.value.subscribe('/displays/dashboard', (message) => {
          didLoadDashboard.value = true;
          setMessage(message.body);
        });
        if (stompClient.value && stompClient.value.connected) {
          stompClient.value.send(
            '/app/displays/request-dashboard',
            JSON.stringify(requestMessage),
            {}
          );
        }
      },
      (error) => {
        console.error('Socket connection error:', error);
        console.info('Reconnecting websocket in 10 seconds.');
        connected.value = false;
        didLoadDashboard.value = false;
        setTimeout(() => connect(), 10000);
      }
    );
  }
};

const setMessage = (msg) => {
  if (JSON.stringify(responseMessage) !== JSON.stringify(JSON.parse(msg))) {
    responseMessage = JSON.parse(msg);
  }
};

const handler = () => {
  if (stompClient.value) {
    stompClient.value.send(
      '/app/displays/disconnect-dashboard',
      JSON.stringify(requestMessage),
      {}
    );
    stompClient.value.disconnect();
  }
  connected.value = false;
};

</script>

<style scoped>
  p {
      color: #3F4349;
  }

  .displays-table {
    margin-top: 1rem;
    width: 100%;
  }

  a.link {
    text-decoration: none;
    font-weight: bold;
  }

  .guide-button {
    border-radius: 14px;
  }

  .refresh-icon {
    height: fit-content;
    margin: 0;
  }
</style>